import { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { BSC_CHAIN_ID } from '../config/contracts';

export interface Web3State {
  provider: ethers.providers.Web3Provider | null;
  signer: ethers.Signer | null;
  account: string | null;
  chainId: number | null;
  isConnected: boolean;
  isConnecting: boolean;
  isBSC: boolean;
  balance: string;
  error: string | null;
}

const BSC_NETWORK_PARAMS = {
  chainId: '0x38',
  chainName: 'BNB Smart Chain',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: ['https://bsc-dataseed1.binance.org/'],
  blockExplorerUrls: ['https://bscscan.com/'],
};

export function useWeb3() {
  const [state, setState] = useState<Web3State>({
    provider: null,
    signer: null,
    account: null,
    chainId: null,
    isConnected: false,
    isConnecting: false,
    isBSC: false,
    balance: '0',
    error: null,
  });

  const updateBalance = useCallback(async (
    provider: ethers.providers.Web3Provider,
    account: string
  ) => {
    try {
      const bal = await provider.getBalance(account);
      const formatted = parseFloat(ethers.utils.formatEther(bal)).toFixed(4);
      setState(prev => ({ ...prev, balance: formatted }));
    } catch {}
  }, []);

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setState(prev => ({ ...prev, error: '请安装 MetaMask 钱包' }));
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send('eth_requestAccounts', []);

      const signer = provider.getSigner();
      const account = await signer.getAddress();
      const network = await provider.getNetwork();
      const chainId = network.chainId;
      const isBSC = chainId === BSC_CHAIN_ID;

      await updateBalance(provider, account);

      setState(prev => ({
        ...prev,
        provider,
        signer,
        account,
        chainId,
        isConnected: true,
        isConnecting: false,
        isBSC,
        error: isBSC ? null : '请切换到 BSC 主网',
      }));
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: err.message || '连接失败',
      }));
    }
  }, [updateBalance]);

  const disconnect = useCallback(() => {
    setState({
      provider: null,
      signer: null,
      account: null,
      chainId: null,
      isConnected: false,
      isConnecting: false,
      isBSC: false,
      balance: '0',
      error: null,
    });
  }, []);

  const switchToBSC = useCallback(async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x38' }],
      });
    } catch (err: any) {
      if (err.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [BSC_NETWORK_PARAMS],
        });
      }
    }
  }, []);

  // 监听账户变化
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        disconnect();
      } else {
        // 直接更新账户，不重新走 connect 流程避免闪屏
        setState(prev => {
          if (!prev.provider) return prev;
          prev.provider.getSigner().getAddress().then(addr => {
            setState(p => ({ ...p, account: addr }));
          }).catch(() => {});
          return prev;
        });
      }
    };

    const handleChainChanged = (chainIdHex: string) => {
      // 不 reload，直接更新 chainId 状态
      const newChainId = parseInt(chainIdHex, 16);
      setState(prev => ({
        ...prev,
        chainId: newChainId,
        isBSC: newChainId === BSC_CHAIN_ID,
        error: newChainId === BSC_CHAIN_ID ? null : '请切换到 BSC 主网',
      }));
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [disconnect]);

  // 自动重连
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' }).then((accounts: string[]) => {
        if (accounts.length > 0) {
          connect();
        }
      });
    }
  }, [connect]);

  return { ...state, connect, disconnect, switchToBSC };
}

// 扩展 Window 接口
declare global {
  interface Window {
    ethereum: any;
  }
}
